import { useGoogleLogin } from '@react-oauth/google';
import { useNavigate, useLocation } from 'react-router-dom';
import { loginWithGoogle } from '../services/userService';
import { useAuth } from '../Context/AuthContext';

/**
 * Shared Google OAuth button handler for Login / Signup pages.
 */
export function useGoogleAuth({ setError, setLoading, setSuccessMessage }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setError?.('');
      setSuccessMessage?.('');
      setLoading?.(true);
      try {
        const result = await loginWithGoogle({
          accessToken: tokenResponse.access_token
        });

        const nextUser = result.user || result;
        if (!nextUser || !(nextUser.email || nextUser.id || nextUser._id)) {
          throw new Error('Google login succeeded but no user was returned');
        }

        login(nextUser, result.token || null);

        // Return to where the user was (state from ProtectedRoute, or ?from= after an expired session)
        const fromQuery = new URLSearchParams(location.search).get('from');
        const from =
          location.state?.from || (fromQuery && fromQuery.startsWith('/') && !fromQuery.startsWith('//') ? fromQuery : null);
        if (from && from !== '/login' && from !== '/signup') {
          navigate(from, { replace: true });
        } else if (nextUser.role === 'admin') {
          navigate('/dashboard', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      } catch (err) {
        setError?.(err.message || 'Google authentication failed. Please try again.');
        console.error('Google auth error:', err);
      } finally {
        setLoading?.(false);
      }
    },
    onError: () => {
      setError?.('Google sign-in was cancelled or failed. Please try again.');
    }
  });

  return googleLogin;
}
